<template>
  <SrpModal :isVisible="props.isVisible" size="large" :isClosable="false" @update:isVisible="isVisible => emit('update:isVisible', isVisible)">
    <template #content>
      <EditAndShareForm
        v-if="activeForm === AllForms.EditAndShare"
        :postToEdit="props.postToEdit"
        :customerId="props.customerId"
        :isLoadingSummaryPayload="isLoadingSummaryPayload"
        :socialShareSummaryPayload="socialShareSummaryPayload"
        :savePostCallback="savePost"
        :isSavingPost="isSavingPost"
        :readonlyMode="props.readonlyMode"
        :sectionsToHide="props.sectionsToHide"
        @requestDownload="isInProgress => (isRequestingDownload = isInProgress)"
        @clickEditImages="activeForm = AllForms.SelectHighlights"
        @clickEditCaption="activeForm = AllForms.EditCaption"
      />

      <HighlightsSelectForm
        v-if="activeForm === AllForms.SelectHighlights"
        ref="domRefHighlightsSelectForm"
        :allAssetIds="socialShareSummaryPayload.assets || []"
        :fallbackAssetIds="props.postToEdit?.fallbackAssetIds || []"
        :selectedAssetIds="props.postToEdit?.assetIds"
        :saveCallBack="
          async assetIds => {
            props.postToEdit.assetIds = assetIds;
            await savePost();
          }
        "
        @cancel="activeForm = AllForms.EditAndShare"
        @save="activeForm = AllForms.EditAndShare"
      />

      <EditCaptionForm
        v-if="activeForm === AllForms.EditCaption"
        ref="domRefEditCaptionForm"
        :captionText="props.postToEdit?.caption"
        :saveCallBack="
          async newCaptionText => {
            props.postToEdit.caption = newCaptionText;
            await savePost();
          }
        "
        @cancel="activeForm = AllForms.EditAndShare"
        @save="activeForm = AllForms.EditAndShare"
      />
    </template>

    <template #footer="{ closeModal }">
      <div style="gap: 12px; display: flex; flex-wrap: wrap">
        <template v-if="activeForm === AllForms.EditAndShare">
          <SrpButton color="gray" fill="outlined" @click="closeModal(false, true)" :isDisabled="areButtonsDisabled">Close</SrpButton>

          <SrpButton
            v-if="!props.sectionsToHide.includes(SocialPostEditAndShareFormSections.MarkAsUsedButton)"
            :isDisabled="areButtonsDisabled || props.readonlyMode"
            @click="
              async () => {
                // Save even if it's already marked as used, since postUrl might have changed
                props.postToEdit.markedUsed = true;
                await savePost();
                closeModal();
              }
            "
          >
            <template #icon>
              <LoadingIcon v-if="isSavingPost" :size="18" />
              <IconEmbedded v-else name="check_3-5" :size="18" />
            </template>
            {{ props.postToEdit?.markedUsed ? "Done" : "Mark as Used" }}
          </SrpButton>
        </template>

        <template v-if="activeForm === AllForms.EditCaption">
          <SrpButton color="gray" fill="outlined" @click="domRefEditCaptionForm?.cancel" :isDisabled="areButtonsDisabled">Cancel</SrpButton>

          <SrpButton @click="domRefEditCaptionForm?.save" :isDisabled="areButtonsDisabled">
            <template #icon>
              <LoadingIcon v-if="isSavingPost" :size="18" />
              <IconEmbedded v-else name="save_2-5" :size="18" />
            </template>
            Save
          </SrpButton>
        </template>

        <template v-if="activeForm === AllForms.SelectHighlights">
          <SrpButton color="gray" fill="outlined" @click="domRefHighlightsSelectForm?.cancel" :isDisabled="areButtonsDisabled">Cancel</SrpButton>

          <SrpButton @click="domRefHighlightsSelectForm?.save" :isDisabled="areButtonsDisabled || props.readonlyMode">
            <template #icon>
              <LoadingIcon v-if="isSavingPost" :size="18" />
              <IconEmbedded v-else name="save_2-5" :size="18" />
            </template>
            Save
          </SrpButton>
        </template>
      </div>
    </template>
  </SrpModal>
</template>

<script lang="ts">
export enum SocialPostEditAndShareFormSections {
  Caption = "caption",
  PhotosAndVideos = "photos and videos",
  WhereWasItUsed = "where was it used",
  CreatorTag = "creator tag",
  MarkAsUsedButton = "mark as used button",
}
</script>

<script setup lang="ts">
import { ref, onMounted, computed } from "vue";
import ShareStopSummaryRepo from "@repos/ShareStopSummaryRepo";
import { MetricSender } from "@helpers/MetricSender";

// Components
import EditAndShareForm from "./EditAndShareForm.vue";
import SrpModal from "@components/ui/SrpModal.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import HighlightsSelectForm from "./HighlightsSelectForm.vue";
import EditCaptionForm from "./EditCaptionForm.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";

// Types
import { SocialMediaPostData, SocialShareSummaryPayload } from "@contracts/shareStop";

defineOptions({
  name: "SocialPostEditAndShareModal",
});

const props = withDefaults(
  defineProps<{
    postToEdit: SocialMediaPostData | null;
    customerId: string;
    readonlyMode?: boolean;
    sectionsToHide?: Array<SocialPostEditAndShareFormSections>;
  }>(),
  {
    postToEdit: null,
    customerId: "",
    readonlyMode: false,
    sectionsToHide: () => [],
  }
);

const emit = defineEmits<{
  (e: "close"): void;
  (e: "update:isVisible", isVisible: boolean): void;
  (e: "savePost"): void;
}>();

// DOM refs
const domRefHighlightsSelectForm = ref<any>(null);
const domRefEditCaptionForm = ref<any>(null);

// Switch between forms =======================================================
enum AllForms {
  EditAndShare,
  SelectHighlights,
  EditCaption,
}

const activeForm = ref<AllForms>(AllForms.EditAndShare);

// Is Requesting download =====================================================
const isRequestingDownload = ref<boolean>(false);

// Load socialShareSummaryPayload =============================================
const isLoadingSummaryPayload = ref<boolean>(false);
const socialShareSummaryPayload = ref<SocialShareSummaryPayload | null>(null);

async function loadShareSummaryPayload() {
  isLoadingSummaryPayload.value = true;
  socialShareSummaryPayload.value = await ShareStopSummaryRepo.loadSocialShareSummaryPayloadFromPostId(props.customerId, props.postToEdit?.id);
  isLoadingSummaryPayload.value = false;
}

onMounted(async () => {
  await loadShareSummaryPayload();

  if (props.postToEdit) {
    MetricSender.sendMetric("Cust-SocialPost-Open", {
      socialPostId: props.postToEdit?.id,
      aiGen: props.postToEdit?.createdWithAI,
      autoGen: props.postToEdit?.autoGenerated,
    });
  }
});

// Save post ==================================================================
const isSavingPost = ref<boolean>(false);

async function savePost() {
  isSavingPost.value = true;
  await ShareStopSummaryRepo.updatePost(props.postToEdit);
  isSavingPost.value = false;

  emit("savePost");
}

// Are the modal buttons disabled =============================================
const areButtonsDisabled = computed(() => {
  return isLoadingSummaryPayload.value || isSavingPost.value || isRequestingDownload.value;
});
</script>

<style scoped lang="scss"></style>
